import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Landing/Navbar";
import { sendOtp } from "../../services/api";
import countryCodes from "../../data/countryCodes";
import CountryCodePicker from "../../Components/CountryCodePicker";

const Login = () => {
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const selectedCountry =
    countryCodes.find((c) => c.code === countryCode) ||
    countryCodes.find((c) => c.code === "91");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!phone || phone.length < 7 || phone.length > selectedCountry.maxDigits) {
      setError(`Enter a valid ${selectedCountry.maxDigits}-digit mobile number.`);
      return;
    }

    const formattedPhone = `${countryCode}${phone}`;
    setLoading(true);

    try {
      const { sessionId } = await sendOtp(formattedPhone);
      sessionStorage.setItem(
        "karankiOtpState",
        JSON.stringify({ sessionId, phone: formattedPhone, authMode: "login" })
      );
      navigate("/verify-otp");
    } catch (err) {
      setError(err.message || "Unable to send OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Login | Karanki Matrimony</title>
        <meta name="description" content="Login to Karanki Matrimony using your mobile number and OTP." />
        <link rel="canonical" href="https://www.karankimatrimony.com/login" />
      </Helmet>

      <Navbar />

      <main className="flex min-h-[calc(100vh-80px)] items-center justify-center px-4 py-10">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="rounded-3xl border border-sand-200 bg-white p-6 shadow-soft sm:p-8">
            <h1 className="text-center text-2xl font-semibold text-ink-800">Login</h1>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label className="field-label">Mobile Number</label>
                <div className="input-group">
                  <CountryCodePicker value={countryCode} onChange={setCountryCode} />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    onFocus={(e) => e.target.scrollIntoView({ behavior: "smooth", block: "center" })}
                    placeholder="Enter mobile number"
                    maxLength={selectedCountry.maxDigits}
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700"
                >
                  {error}
                </motion.div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-[15px]">
                {loading ? "Sending OTP..." : "Send OTP"}
                {!loading && <ChevronRight size={16} />}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-ink-500">
              New user?{" "}
              <Link to="/register" className="font-medium text-brand-600 hover:underline">
                Register
              </Link>
            </p>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default Login;
